import React, {ReactNode} from 'react'
import {FormattedNumber, useIntl} from 'react-intl'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Text, TitleVariant} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {FormattedMessageWrapper} from '@vio/core/src/localization/components/FormattedMessage'

import {PriceSize} from '../../search/Price'
import {loyaltyMessages} from '../loyaltyMessages'

export interface PointsPriceParts {
  /** Points that go towards the price */
  pointsPart: number
  /** What is left to pay in the display currency */
  cashPart: number
  /** Display currency of the cash part */
  currencyCode: string
}

interface Props extends PointsPriceParts {
  /** Whether to caption the price with the points it would take. The map card has no room for it. */
  showMaxPointsCaption?: boolean
  /** Size of the points figure, matching the currency price it replaces */
  size?: PriceSize
  /** Identify the element for selection in integration tests, FullStory, etc. */
  dataId?: string
  /** Which line edge the block hugs. Mobile cards hug the end, the rest the start. */
  alignment?: 'start' | 'end'
  /** Greys the whole price out, for a sold-out offer */
  isMuted?: boolean
  /** Short label such as "/ night" that never wraps away from the cash part */
  suffix?: ReactNode
}

const pointsVariant: Record<PriceSize, TitleVariant> = {
  sm: 'titleS',
  md: 'titleM',
  lg: 'titleL',
  xl: 'titleXL'
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  &.--to-line-end {
    align-items: flex-end;
  }

  &.--muted * {
    color: ${cssTheme.colors.content.neutral.disabled};
  }
`

const Caption = styled(Text)`
  color: ${cssTheme.colors.content.neutral.secondary};
`

const Amounts = styled(Text)`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  column-gap: ${cssTheme.layout.spacing.s200};

  &.--to-line-end {
    justify-content: flex-end;
  }
`

const CashPart = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: ${cssTheme.layout.spacing.s100};
  white-space: nowrap;
`

const Inline = styled.span`
  white-space: nowrap;
`

/**
 * The values for the `loyalty.pointsPrice*` messages. The cash part is a
 * formatted string, not a `Price`, so it flows inline with the text.
 */
export const usePointsPriceValues = () => {
  const {formatNumber} = useIntl()

  return (
    {pointsPart, cashPart, currencyCode}: PointsPriceParts,
    figureVariant?: TitleVariant
  ) => {
    const cashAmount = formatNumber(cashPart, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })

    return {
      count: pointsPart,
      points: figureVariant ? (
        <Text variant={figureVariant} tag="span">
          <FormattedNumber value={pointsPart} />
        </Text>
      ) : (
        <FormattedNumber value={pointsPart} />
      ),
      cashAmount: figureVariant ? (
        <Text variant="titleS" tag="span">
          {cashAmount}
        </Text>
      ) : (
        cashAmount
      ),
      cash: (chunks: ReactNode) =>
        figureVariant ? <CashPart>{chunks}</CashPart> : <>{chunks}</>
    }
  }
}

export const PointsPrice = ({
  pointsPart,
  cashPart,
  currencyCode,
  showMaxPointsCaption = true,
  size = 'lg',
  dataId,
  alignment,
  isMuted = false,
  suffix
}: Props) => {
  const {isMobile} = useDeviceLayout()
  const pointsPriceValues = usePointsPriceValues()
  const toLineEnd = cx(
    (alignment ?? (isMobile ? 'end' : 'start')) === 'end' && '--to-line-end'
  )
  const values = pointsPriceValues(
    {pointsPart, cashPart, currencyCode},
    pointsVariant[size]
  )

  return (
    <Wrapper
      className={cx(toLineEnd, isMuted && '--muted')}
      data-id={dataId ?? 'PointsPrice'}
    >
      {showMaxPointsCaption && (
        <Caption variant="bodyXS">
          <FormattedMessageWrapper {...loyaltyMessages.maxPointsToRedeem} />
        </Caption>
      )}
      <Amounts variant="labelXS" tag="div" className={toLineEnd}>
        <FormattedMessageWrapper
          {...loyaltyMessages.pointsPrice}
          values={{
            ...values,
            cash: (chunks: ReactNode) =>
              values.cash(
                <>
                  {chunks}
                  {suffix}
                </>
              )
          }}
        />
      </Amounts>
    </Wrapper>
  )
}

/** The same split as running text, for price breakdowns: "23,120 points + €0" */
export const PointsPriceText = (parts: PointsPriceParts) => {
  const pointsPriceValues = usePointsPriceValues()

  return (
    <Inline>
      <FormattedMessageWrapper
        {...loyaltyMessages.pointsPrice}
        values={pointsPriceValues(parts)}
      />
    </Inline>
  )
}
